import type { CSSProperties } from "react"
import type { ColumnDefinition } from "../types"
import { DEFAULT_MIN_COLUMN_WIDTH } from "../constants"

export type RowLayout = {
  width: number
  template: string
  hasFractional: boolean
}

export function getRowLayout(columns: ColumnDefinition[]): RowLayout {
  let column_templates: string[] = []
  let width = 0
  let fractionalCols = columns.length
  columns.forEach((column) => {
    if (column.fixed && column.width === undefined) {
      throw new Error(
        `Definition for column ${column.id} is wrong. Column is 'fixed', set 'width' explicitly.`,
      )
    }
    if (column.width === undefined) {
      const min_w = column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH
      column_templates.push(`minmax(${min_w}px, 1fr)`)
      width += min_w
    } else {
      column_templates.push("auto")
      width += column.width
      fractionalCols -= 1
    }
  })

  // if all colums have width set, total row width should be the sum
  // else if we have columns with fractional width, width should be treated as min-width

  return {
    hasFractional: fractionalCols > 0,
    width,
    template: column_templates.join(" ").trim(),
  }
}

export function getLayoutVariables(
  fixedColumns: ColumnDefinition[],
  mainColumns: ColumnDefinition[],
): CSSProperties {
  const hasFixedColumns = fixedColumns.length > 0
  const { template: fixedTemplate, width: totalFixedColumnsWidth } =
    getRowLayout(fixedColumns)

  if (hasFixedColumns && totalFixedColumnsWidth === 0) {
    throw new Error("You probably forgot to set 'width' in 'fixed' columns")
  }

  // main
  const {
    template: mainTemplate,
    width: mainWidth,
    hasFractional,
  } = getRowLayout(mainColumns)

  const containerTemplate = hasFixedColumns
    ? `${totalFixedColumnsWidth}px 1fr`
    : "1fr"

  const vars = {
    "--container-template": containerTemplate,
    // fixed
    "--fixed-data-grid-width": `${totalFixedColumnsWidth}px`,
    "--fixed-row-template": fixedTemplate,
    "--fixed-row-width": `${totalFixedColumnsWidth}px`,
    "--fixed-row-min-width": `${totalFixedColumnsWidth}px`,
    // main
    "--main-data-grid-width": "100%",
    "--main-row-template": mainTemplate,
    "--main-row-width": hasFractional ? "100%" : `${mainWidth}px`,
    "--main-row-min-width": `${mainWidth}px`,
  } as React.CSSProperties

  return vars
}
